import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import format from 'date-fns/format';
import addMonths from 'date-fns/addMonths';
import subMonths from 'date-fns/subMonths';
import subYears from 'date-fns/subYears';
import { convertDate } from 'utils/date';
import type { RootState } from './configureStore';
import type { YieldsStoreType } from './types/yields-store';

const currentMonth = convertDate(new Date(), 'MM/01/yyyy');
const oneMonthAgo = convertDate(subMonths(new Date(), 1), 'MM/01/yyyy');
const elevenMonthsAgo = convertDate(subMonths(new Date(), 11), 'MM/01/yyyy');
const oneYearAgo = convertDate(subYears(new Date(), 1), 'MM/dd/yyyy');

export const slice = createSlice({
  name: 'yields',
  initialState: {
    graph: {
      datePicker: {
        start: {
          default: oneMonthAgo,
          value: oneMonthAgo,
          min: oneYearAgo,
          max: oneMonthAgo,
        },
        end: {
          default: currentMonth,
          value: currentMonth,
          min: elevenMonthsAgo,
          max: currentMonth,
        },
      },
    },
  },
  reducers: {
    startDateSelected: (
      yields: YieldsStoreType,
      action: PayloadAction<string>,
    ) => {
      const { datePicker } = yields.graph;
      datePicker.start.value = action.payload;
      datePicker.end.min = format(
        addMonths(new Date(action.payload), 1),
        'MM/dd/yyyy',
      );
    },
    endDateSelected: (
      yields: YieldsStoreType,
      action: PayloadAction<string>,
    ) => {
      const { datePicker } = yields.graph;
      datePicker.end.value = action.payload;
      datePicker.start.max = format(
        subMonths(new Date(action.payload), 1),
        'MM/dd/yyyy',
      );
    },
    setMinDate: (
      yields: YieldsStoreType,
      action: PayloadAction<{ minStart: string; minEnd: string }>,
    ) => {
      const { datePicker } = yields.graph;
      datePicker.start.min = action.payload.minStart;
      datePicker.start.value = action.payload.minStart;
      datePicker.end.min = action.payload.minEnd;
    },
  },
});

export const { startDateSelected, endDateSelected, setMinDate } = slice.actions;
export default slice.reducer;

// Selectors
export const getDatePicker = (state: RootState) =>
  state.entities.yields.graph.datePicker;
